import React, { useState, useEffect } from 'react';
import { sessionHeaders } from '../services/auth';

const NEON = '/.netlify/functions/neon';

function neonQuery(query, params = {}) {
  return fetch(NEON, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
    body: JSON.stringify({ query, params }),
  }).then(r => r.json());
}

function fmt$(n) {
  if (n == null) return '—';
  return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const EVENT_COLORS = {
  purchase:     { bg: '#e6f9f0', color: '#1a8a4a' },
  new_customer: { bg: '#e8f0fe', color: '#00395C' },
  newsletter:   { bg: '#fff8e6', color: '#b06a00' },
  sms:          { bg: '#f3e8ff', color: '#7c3aed' },
  vip_join:     { bg: '#fde8e8', color: '#c0392b' },
};

function EventBadge({ type }) {
  const s = EVENT_COLORS[type] || { bg: '#f5f5f5', color: '#5f6368' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
      background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '0.5px',
      whiteSpace: 'nowrap',
    }}>
      {type?.replace(/_/g, ' ') || 'unknown'}
    </span>
  );
}

function SourceBadge({ source }) {
  const colors = {
    bigcommerce: { bg: '#e8f0fe', color: '#00395C' },
    dotdigital:  { bg: '#fff8e6', color: '#b06a00' },
    nightly_sync: { bg: '#f5f5f5', color: '#5f6368' },
  };
  const s = colors[source] || { bg: '#f5f5f5', color: '#5f6368' };
  return (
    <span style={{
      fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 8,
      background: s.bg, color: s.color,
    }}>
      {source || '—'}
    </span>
  );
}

export default function Conversions() {
  const [summary, setSummary] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      neonQuery('conversions_summary'),
      neonQuery('conversions_recent', { limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    ])
      .then(([sumRes, evtRes]) => {
        if (sumRes.error) throw new Error(sumRes.error);
        if (evtRes.error) throw new Error(evtRes.error);
        setSummary(sumRes.rows || []);
        setEvents(evtRes.rows || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [page]);

  // Load more events when filter/page changes
  useEffect(() => {
    if (page === 0) return; // already loaded on mount
    setLoading(true);
    neonQuery('conversions_recent', { limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then(res => {
        if (res.error) throw new Error(res.error);
        setEvents(prev => [...prev, ...(res.rows || [])]);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [page]);

  const filteredEvents = filter === 'all' ? events : events.filter(e => e.event_type === filter);

  const totalValue = summary.reduce((acc, s) => acc + parseFloat(s.total_value || 0), 0);
  const totalCount = summary.reduce((acc, s) => acc + parseInt(s.total_count || 0), 0);

  const thStyle = { textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)', fontSize: 11 };
  const tdStyle = { padding: '7px 12px', fontSize: 12 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Conversions</h2>
      </div>

      {error && (
        <div style={{ padding: '8px 14px', background: '#fde8e8', borderRadius: 6, fontSize: 12, color: '#c0392b', marginBottom: 12 }}>
          Error: {error}
        </div>
      )}

      {/* Summary KPIs */}
      {!loading && summary.length > 0 && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="kpi-label">Total Events</div>
            <div className="kpi-value">{totalCount.toLocaleString()}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Total Value</div>
            <div className="kpi-value">{fmt$(totalValue)}</div>
          </div>
          {summary.map(s => (
            <div className="kpi-card" key={s.event_type}>
              <div className="kpi-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <EventBadge type={s.event_type} />
              </div>
              <div className="kpi-value" style={{ fontSize: 18 }}>
                {parseInt(s.total_count).toLocaleString()}
                <span style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 400, marginLeft: 6 }}>
                  {fmt$(s.total_value)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {['all', ...summary.map(s => s.event_type)].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              fontSize: 11, fontWeight: filter === f ? 700 : 500, padding: '4px 12px', borderRadius: 14,
              border: filter === f ? '2px solid var(--orange)' : '1px solid var(--gray-200)',
              background: filter === f ? 'var(--orange)' : '#fff',
              color: filter === f ? '#fff' : 'var(--navy)',
              cursor: 'pointer', fontFamily: 'Montserrat',
            }}
          >
            {f === 'all' ? 'All' : f.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {/* Events table */}
      <div className="card">
        <div className="card-header">Recent Events</div>
        {loading && events.length === 0 ? (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>
        ) : filteredEvents.length === 0 ? (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No conversions found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Event</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Order</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
                  <th style={thStyle}>Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((e, i) => (
                  <tr key={e.id || i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: 'var(--gray-600)' }}>{fmtDate(e.created_at)}</td>
                    <td style={tdStyle}><EventBadge type={e.event_type} /></td>
                    <td style={{ ...tdStyle, color: 'var(--navy)', fontWeight: 500 }}>{e.customer_email || '—'}</td>
                    <td style={{ ...tdStyle, color: 'var(--gray-600)' }}>{e.order_id || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--navy)' }}>{fmt$(e.value)}</td>
                    <td style={tdStyle}><SourceBadge source={e.source} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Load more */}
        {filteredEvents.length >= PAGE_SIZE && (
          <div style={{ padding: 12, textAlign: 'center' }}>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={loading}
              style={{
                fontSize: 11, fontWeight: 600, padding: '6px 20px', borderRadius: 6,
                border: '1px solid var(--gray-200)', background: '#fff', color: 'var(--navy)',
                cursor: 'pointer', fontFamily: 'Montserrat',
              }}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
